"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  BookOpen,
  Building2,
  Users,
  ShieldAlert,
  UserCircle,
  Wallet,
  Landmark,
  Lock,
  Bell,
  PanelLeftClose,
  PanelLeftOpen,
  AlertTriangle,
  CheckCircle2,
  X,
  Sun,
  Moon,
  Settings,
  Receipt,
  Banknote,
  Fingerprint,
  Scale,
} from "lucide-react";
import { Logo } from "./logo";
import { useSidebar } from "@/contexts/sidebar-context";
import { useTheme } from "@/contexts/theme-context";
import { alerts } from "@/lib/mock-data";
import { timeAgo } from "@/lib/utils";

const EXPANDED = 220;
const COLLAPSED = 68;

const navItems = [
  { href: "/", label: "Dashboard", icon: LayoutDashboard },
  { href: "/ledger", label: "Trade Ledger", icon: BookOpen },
  { href: "/bank-ledger", label: "Bank Ledger", icon: Building2 },
  { href: "/accounts", label: "Accounts", icon: Receipt },
  { href: "/reconciliation", label: "Reconciliation", icon: Scale },
  { href: "/kyc", label: "KYC Reviews", icon: Fingerprint, dividerAfter: true },
  { href: "/counterparties", label: "Counterparties", icon: Users },
  { href: "/blacklist", label: "Dantan's Blacklist", icon: ShieldAlert },
  { href: "/source-of-funds", label: "Source of Funds", icon: Banknote, dividerAfter: true },
  { href: "/profile", label: "My Profile", icon: UserCircle },
  { href: "/wallets", label: "My Wallets", icon: Wallet },
  { href: "/treasury", label: "Treasury", icon: Landmark },
  { href: "/locks", label: "Locks", icon: Lock, dividerAfter: true },
  { href: "/admin", label: "Admin", icon: Settings },
] as const;

const spring = { type: "spring" as const, stiffness: 400, damping: 30 };
const gentleSpring = { type: "spring" as const, stiffness: 300, damping: 25 };

const navItemVariants = {
  hidden: { opacity: 0, x: -8 },
  visible: (i: number) => ({
    opacity: 1,
    x: 0,
    transition: {
      delay: 0.03 * i,
      duration: 0.3,
      ease: [0.23, 1, 0.32, 1] as const,
    },
  }),
};

function SidebarContent({ onNavClick }: { onNavClick?: () => void }) {
  const pathname = usePathname();
  const { collapsed, toggle, isMobile, showLabels, visualWidth, setHoverExpanded } = useSidebar();
  const { theme, toggleTheme } = useTheme();
  const [notifOpen, setNotifOpen] = useState(false);
  const [readAlerts, setReadAlerts] = useState<Set<number>>(new Set());
  const notifRef = useRef<HTMLDivElement>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Click-outside to close notification dropdown
  useEffect(() => {
    if (!notifOpen) return;
    function handleMouseDown(e: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    }
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [notifOpen]);

  // Cmd+B keyboard shortcut (desktop only)
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "b") {
        e.preventDefault();
        toggle();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [toggle]);

  const hasUnread = alerts.some((a) => !readAlerts.has(a.id));

  const alertIcons: Record<string, React.ReactNode> = {
    warning: <AlertTriangle className="w-3.5 h-3.5 text-warning flex-shrink-0" />,
    danger: <ShieldAlert className="w-3.5 h-3.5 text-danger flex-shrink-0" />,
    success: <CheckCircle2 className="w-3.5 h-3.5 text-accent flex-shrink-0" />,
    info: <Bell className="w-3.5 h-3.5 text-muted flex-shrink-0" />,
  };

  // Hover-to-expand handlers (desktop collapsed only)
  const handleMouseEnter = useCallback(() => {
    if (isMobile || !collapsed) return;
    hoverTimerRef.current = setTimeout(() => {
      setHoverExpanded(true);
    }, 200);
  }, [isMobile, collapsed, setHoverExpanded]);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setHoverExpanded(false);
  }, [setHoverExpanded]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    };
  }, []);

  return (
    <motion.aside
      initial={{ x: isMobile ? -EXPANDED : 0, opacity: isMobile ? 0 : 1 }}
      animate={{ x: 0, opacity: 1 }}
      transition={spring}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="h-screen flex flex-col z-50 overflow-hidden"
      style={{
        width: visualWidth,
        transition: "width 0.25s cubic-bezier(0.23, 1, 0.32, 1)",
        willChange: "width",
        background: "var(--surface)",
        backdropFilter: "blur(16px) saturate(140%)",
        WebkitBackdropFilter: "blur(16px) saturate(140%)",
        borderRight: "1px solid var(--border)",
      }}
    >
      {/* Logo */}
      <div
        className="h-14 px-3 flex items-center gap-2.5 flex-shrink-0"
        style={{ borderBottom: "1px solid var(--border)" }}
      >
        <motion.div
          className="flex-shrink-0 w-8 flex items-center justify-center"
          whileHover={{ scale: 1.08, rotate: 6 }}
          transition={gentleSpring}
        >
          <Logo size={26} />
        </motion.div>
        <AnimatePresence>
          {showLabels && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18, delay: 0.08 }}
              className="flex flex-col overflow-hidden whitespace-nowrap"
            >
              <span className="text-[13px] font-bold text-foreground tracking-tight leading-none">
                <span>Chain</span><span className="text-[#0d9488]">Filer</span>
              </span>
              <span className="text-[9px] text-muted uppercase tracking-[0.12em] mt-0.5">
                On-Chain Intelligence
              </span>
            </motion.div>
          )}
        </AnimatePresence>
        {/* Mobile close button */}
        {isMobile && (
          <button
            aria-label="Close menu"
            onClick={onNavClick}
            className="btn-icon ml-auto p-1 rounded-md text-muted hover:text-foreground transition-all duration-150"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 py-3 px-2 overflow-y-auto overflow-x-hidden">
        {navItems.map((item, i) => {
          const isActive =
            pathname === item.href ||
            (item.href !== "/" && pathname.startsWith(item.href));
          const Icon = item.icon;
          const isCollapsedIcons = collapsed && !showLabels;
          return (
            <motion.div
              key={item.href}
              custom={i}
              variants={navItemVariants}
              initial="hidden"
              animate="visible"
            >
              <Link
                href={item.href}
                onClick={onNavClick}
                aria-current={isActive ? "page" : undefined}
                className="group relative flex items-center gap-2.5 px-2.5 py-2.5 rounded-md text-[13px] mb-1 transition-colors duration-150"
              >
                {isActive && (
                  <motion.div
                    layoutId="nav-active"
                    className="absolute inset-0 rounded-md"
                    style={{ background: "var(--glass-bg)", border: "1px solid var(--glass-border)" }}
                    transition={{ type: "spring", stiffness: 350, damping: 30 }}
                  />
                )}
                <motion.div
                  className={`flex-shrink-0 flex items-center justify-center relative z-10 ${isCollapsedIcons ? "w-[18px]" : "w-4"}`}
                  whileHover={{ scale: 1.18, rotate: isActive ? 0 : -8, y: -1 }}
                  whileTap={{ scale: 0.92 }}
                  transition={{ type: "spring", stiffness: 500, damping: 15, mass: 0.6 }}
                >
                  <Icon
                    className={`${isCollapsedIcons ? "w-[18px] h-[18px]" : "w-4 h-4"} transition-all duration-150 ${isActive ? "text-accent" : "text-muted group-hover:text-foreground"}`}
                    strokeWidth={isActive ? 2 : 1.5}
                  />
                </motion.div>
                <AnimatePresence>
                  {showLabels && (
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.18, delay: 0.08 }}
                      className={`relative z-10 overflow-hidden whitespace-nowrap ${
                        isActive
                          ? "text-foreground font-medium"
                          : "text-muted group-hover:text-foreground"
                      }`}
                    >
                      {item.label}
                    </motion.span>
                  )}
                </AnimatePresence>

                {/* Tooltip (collapsed mode only, desktop, not hover-expanded) */}
                {!isMobile && collapsed && !showLabels && (
                  <div
                    className="absolute left-full ml-2 px-2 py-1 rounded-md text-[12px] text-foreground whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-50"
                    style={{
                      background: "var(--surface-2)",
                      backdropFilter: "blur(12px)",
                      border: "1px solid var(--border-hover)",
                    }}
                  >
                    {item.label}
                  </div>
                )}
              </Link>
              {/* Group divider */}
              {"dividerAfter" in item && item.dividerAfter && (
                <div className="my-2 mx-2.5 h-px bg-border/50" />
              )}
            </motion.div>
          );
        })}
      </nav>

      {/* Bottom section */}
      <div className="flex-shrink-0">
        {/* Theme Toggle */}
        <div className="px-2 py-1.5" style={{ borderTop: "1px solid var(--border)" }}>
          <button
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            onClick={toggleTheme}
            className="btn-ghost group relative w-full flex items-center gap-2.5 px-2.5 py-2.5 rounded-md text-[13px] transition-all duration-150 cursor-pointer"
          >
            <motion.div
              className="flex-shrink-0 w-4 flex items-center justify-center"
              whileHover={{ scale: 1.15, rotate: 15 }}
              transition={gentleSpring}
            >
              <AnimatePresence mode="wait" initial={false}>
                {theme === "dark" ? (
                  <motion.div
                    key="sun"
                    initial={{ scale: 0, rotate: -90, opacity: 0 }}
                    animate={{ scale: 1, rotate: 0, opacity: 1 }}
                    exit={{ scale: 0, rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Sun className="w-4 h-4" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="moon"
                    initial={{ scale: 0, rotate: 90, opacity: 0 }}
                    animate={{ scale: 1, rotate: 0, opacity: 1 }}
                    exit={{ scale: 0, rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Moon className="w-4 h-4" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
            <AnimatePresence>
              {showLabels && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.18, delay: 0.08 }}
                  className="overflow-hidden whitespace-nowrap"
                >
                  {theme === "dark" ? "Light Mode" : "Dark Mode"}
                </motion.span>
              )}
            </AnimatePresence>

            {!isMobile && collapsed && !showLabels && (
              <div
                className="absolute left-full ml-2 px-2 py-1 rounded-md text-[12px] text-foreground whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-50"
                style={{
                  background: "var(--surface-2)",
                  border: "1px solid var(--border-hover)",
                }}
              >
                {theme === "dark" ? "Light Mode" : "Dark Mode"}
              </div>
            )}
          </button>
        </div>

        {/* Toggle Button (desktop only) */}
        {!isMobile && (
          <div className="px-2 py-1.5 flex-shrink-0">
            <motion.button
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-expanded={!collapsed}
              onClick={toggle}
              whileTap={{ scale: 0.97 }}
              className="btn-ghost group relative w-full flex items-center gap-2.5 px-2.5 py-2.5 rounded-md text-[13px] transition-all duration-150 cursor-pointer"
            >
              <motion.div
                className="flex-shrink-0 w-4 flex items-center justify-center"
                animate={{ rotate: collapsed ? 0 : 180 }}
                transition={gentleSpring}
              >
                {collapsed ? (
                  <PanelLeftOpen className="w-4 h-4" />
                ) : (
                  <PanelLeftClose className="w-4 h-4" />
                )}
              </motion.div>
              <AnimatePresence>
                {showLabels && (
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.18, delay: 0.08 }}
                    className="overflow-hidden whitespace-nowrap"
                  >
                    Collapse
                  </motion.span>
                )}
              </AnimatePresence>

              {!isMobile && collapsed && !showLabels && (
                <div
                  className="absolute left-full ml-2 px-2 py-1 rounded-md text-[12px] text-foreground whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-50"
                  style={{
                    background: "var(--surface-2)",
                    border: "1px solid var(--border-hover)",
                  }}
                >
                  Expand
                </div>
              )}
            </motion.button>
          </div>
        )}

        {/* User */}
        <div className="px-2 py-3 flex-shrink-0" style={{ borderTop: "1px solid var(--border)" }}>
          <div className="flex items-center gap-2.5 px-2.5">
            <motion.div
              className="flex-shrink-0 w-7 h-7 rounded-full bg-accent/15 flex items-center justify-center text-[10px] font-bold text-accent"
              whileHover={{ scale: 1.1 }}
              transition={gentleSpring}
            >
              JT
            </motion.div>
            <AnimatePresence>
              {showLabels && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.18, delay: 0.08 }}
                  className="flex-1 min-w-0 overflow-hidden whitespace-nowrap"
                >
                  <p className="text-[13px] font-medium text-foreground truncate">Jeff Trading</p>
                  <p className="text-[10px] text-muted">Premium</p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Notification Bell + Dropdown */}
            <AnimatePresence>
              {showLabels && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ opacity: { duration: 0.18, delay: 0.08 } }}
                  ref={notifRef}
                  className="relative flex-shrink-0"
                >
                  <motion.button
                    aria-label="Notifications"
                    aria-expanded={notifOpen}
                    onClick={() => setNotifOpen(!notifOpen)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    transition={gentleSpring}
                    className="btn-icon relative p-1 rounded-md text-muted hover:text-foreground transition-all duration-150 cursor-pointer"
                  >
                    <Bell className="w-3.5 h-3.5" />
                    {hasUnread && (
                      <motion.span
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-danger"
                      />
                    )}
                  </motion.button>

                  {/* Notification Dropdown */}
                  <AnimatePresence>
                    {notifOpen && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 4 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 4 }}
                        transition={{ duration: 0.15 }}
                        className="absolute bottom-full right-0 mb-2 w-72 z-[60] rounded-lg overflow-hidden"
                        style={{
                          background: "var(--surface)",
                          backdropFilter: "blur(20px)",
                          WebkitBackdropFilter: "blur(20px)",
                          border: "1px solid var(--glass-border)",
                          boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
                        }}
                      >
                        {/* Header */}
                        <div className="px-3 py-2.5 flex items-center justify-between" style={{ borderBottom: "1px solid var(--border)" }}>
                          <span className="text-[13px] font-semibold text-foreground">Notifications</span>
                          <button
                            onClick={() => setReadAlerts(new Set(alerts.map((a) => a.id)))}
                            className="btn-inline-action text-[11px] text-accent hover:text-accent-hover px-1.5 py-0.5 rounded transition-all duration-150 cursor-pointer"
                          >
                            Mark all read
                          </button>
                        </div>

                        {/* Alert rows */}
                        <div className="max-h-64 overflow-y-auto">
                          {alerts.map((alert) => {
                            const isRead = readAlerts.has(alert.id);
                            return (
                              <div
                                key={alert.id}
                                className="px-3 py-2.5 flex items-start gap-2.5 transition-colors duration-100 hover:bg-surface-2 cursor-pointer"
                                style={{ borderBottom: "1px solid var(--border)" }}
                                onClick={() => setReadAlerts((prev) => new Set([...prev, alert.id]))}
                              >
                                <div className="mt-0.5">{alertIcons[alert.type]}</div>
                                <div className="flex-1 min-w-0">
                                  <p className={`text-[12px] leading-snug ${isRead ? "text-muted" : "text-foreground font-semibold"}`}>
                                    {alert.title}
                                  </p>
                                  <p className="text-[11px] text-muted line-clamp-2 mt-0.5">{alert.body}</p>
                                  <p className="text-[10px] text-muted/60 mt-1">{timeAgo(alert.createdAt)}</p>
                                </div>
                                {!isRead && (
                                  <div className="w-1.5 h-1.5 rounded-full bg-accent mt-1.5 flex-shrink-0" />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Collapse hint (desktop only) */}
          {!isMobile && (
            <AnimatePresence>
              {!collapsed && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ opacity: { duration: 0.18, delay: 0.08 } }}
                  className="text-[10px] text-muted/40 text-center mt-2 whitespace-nowrap"
                >
                  <kbd className="kbd">&#8984;B</kbd> to collapse
                </motion.p>
              )}
            </AnimatePresence>
          )}
        </div>
      </div>
    </motion.aside>
  );
}

export function Sidebar() {
  const { isMobile, mobileOpen, setMobileOpen } = useSidebar();

  if (isMobile) {
    return (
      <AnimatePresence>
        {mobileOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              key="sidebar-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
              onClick={() => setMobileOpen(false)}
            />
            {/* Sidebar */}
            <motion.div
              key="sidebar-mobile"
              initial={{ x: -EXPANDED }}
              animate={{ x: 0 }}
              exit={{ x: -EXPANDED }}
              transition={spring}
              className="fixed top-0 left-0 z-50"
            >
              <SidebarContent onNavClick={() => setMobileOpen(false)} />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    );
  }

  return (
    <div className="fixed top-0 left-0 z-50">
      <SidebarContent />
    </div>
  );
}
